import React, { useEffect, useState } from "react";
import GoogleAddressAutocomplete from "../../../components/forms/GoogleAddressAutocomplete";
import {
  createAddress,
  deleteAddress,
  fetchMyAddresses,
  setDefaultAddress,
  updateAddress,
} from "../../../api/userAddresses";
import logger from "../../../utils/logger";

const EMPTY_FORM = {
  addressSearch: "",
  street: "",
  neighborhood: "",
  city: "",
  address_text: "",
  place_id: "",
};

export default function AddressesTab({ isAr = false }) {
  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [googleReady, setGoogleReady] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const labels = {
    title: isAr ? "عناويني" : "My Addresses",
    subtitle: isAr
      ? "احفظ عنوانًا حقيقيًا ومنظمًا، ثم اختر واحدًا كعنوان افتراضي."
      : "Save a real, structured address and choose one as your default.",
    addTitle: editingId
      ? isAr
        ? "تعديل العنوان"
        : "Edit address"
      : isAr
        ? "إضافة عنوان جديد"
        : "Add a new address",
    search: isAr ? "ابحث عن عنوانك" : "Search your address",
    searchHint: isAr
      ? "ابدأ بالبحث ثم اختر عنوانًا حقيقيًا من الاقتراحات."
      : "Start searching and choose a real address suggestion.",
    searchLoading: isAr ? "جارٍ تحميل اقتراحات العنوان..." : "Loading address suggestions...",
    searchNoResults: isAr ? "لا توجد نتائج مطابقة." : "No matching addresses found.",
    searchFailed: isAr ? "خدمة البحث عن العنوان غير متاحة مؤقتًا." : "Address search is temporarily unavailable.",
    street: isAr ? "الشارع" : "Street",
    neighborhood: isAr ? "الحي / المنطقة" : "Neighborhood / Area",
    city: isAr ? "المدينة" : "City",
    notes: isAr ? "تفاصيل إضافية" : "Extra details",
    notesHint: isAr ? "شقة، طابق، تعليمات تسليم..." : "Apartment, floor, delivery notes...",
    preview: isAr ? "معاينة العنوان" : "Address preview",
    noPreview: isAr ? "اختر عنوانًا حقيقيًا أو أكمل الحقول ليظهر هنا." : "Choose a real address or complete the fields to preview it here.",
    defaultLabel: isAr ? "افتراضي" : "Default",
    makeDefault: isAr ? "اجعله افتراضيًا" : "Make Default",
    empty: isAr ? "لا توجد عناوين محفوظة بعد." : "No saved addresses yet.",
    save: editingId
      ? isAr
        ? "تحديث العنوان"
        : "Update Address"
      : isAr
        ? "إضافة عنوان"
        : "Add Address",
    saving: isAr ? "جارٍ الحفظ..." : "Saving...",
    cancel: isAr ? "إلغاء التعديل" : "Cancel Edit",
    edit: isAr ? "تعديل" : "Edit",
    delete: isAr ? "حذف" : "Delete",
    added: isAr ? "تمت إضافة العنوان." : "Address added.",
    updated: isAr ? "تم تحديث العنوان." : "Address updated.",
    deleted: isAr ? "تم حذف العنوان." : "Address deleted.",
    defaultUpdated: isAr ? "تم تعيين العنوان الافتراضي." : "Default address updated.",
  };

  async function loadAddresses() {
    try {
      setLoading(true);
      setError("");
      const nextAddresses = await fetchMyAddresses();
      setAddresses(Array.isArray(nextAddresses) ? nextAddresses : []);
    } catch (loadError) {
      logger.error("Addresses load error", loadError);
      setError(isAr ? "فشل تحميل العناوين." : "Failed to load addresses.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAddresses();
  }, [isAr]);

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function handleAddressSearchChange(value) {
    setForm((current) => ({
      ...current,
      addressSearch: value,
      street: "",
      neighborhood: "",
      city: "",
      place_id: "",
    }));
  }

  function handleAddressSelect(selection) {
    setForm((current) => ({
      ...current,
      addressSearch: selection.address || current.addressSearch,
      street: selection.streetAddress || "",
      neighborhood: selection.neighborhood || "",
      city: selection.city || "",
      place_id: selection.placeId || "",
    }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingId(null);
  }

  function handleEdit(address) {
    setEditingId(address.id);
    setForm({
      addressSearch:
        [address.street, address.neighborhood, address.city].filter(Boolean).join(", ") ||
        address.address_text ||
        "",
      street: address.street || "",
      neighborhood: address.neighborhood || "",
      city: address.city || "",
      address_text: address.address_text || "",
      place_id: address.place_id || "",
    });
    setMessage("");
    setError("");
  }

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      setSaving(true);
      setMessage("");
      setError("");

      if (editingId) {
        await updateAddress(editingId, form);
        setMessage(labels.updated);
      } else {
        await createAddress(form);
        setMessage(labels.added);
      }

      resetForm();
      await loadAddresses();
    } catch (submitError) {
      logger.error("Address save error", submitError);
      setError(
        submitError?.message ||
          (isAr ? "تعذر حفظ العنوان." : "Could not save the address.")
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(addressId) {
    try {
      setError("");
      setMessage("");
      await deleteAddress(addressId);
      if (String(editingId) === String(addressId)) resetForm();
      setMessage(labels.deleted);
      await loadAddresses();
    } catch (deleteError) {
      logger.error("Address delete error", deleteError);
      setError(isAr ? "تعذر حذف العنوان." : "Could not delete the address.");
    }
  }

  async function handleSetDefault(addressId) {
    try {
      setError("");
      setMessage("");
      await setDefaultAddress(addressId);
      setMessage(labels.defaultUpdated);
      await loadAddresses();
    } catch (defaultError) {
      logger.error("Set default address error", defaultError);
      setError(
        isAr ? "تعذر تعيين العنوان الافتراضي." : "Could not set the default address."
      );
    }
  }

  const defaultAddress = addresses.find((address) => Boolean(address.is_default)) || addresses[0] || null;
  const addressPreview = [form.street, form.neighborhood, form.city].filter(Boolean).join(", ");

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header fw-semibold">
        {labels.title}
      </div>
      <div className="card-body">
        {message && <div className="alert alert-success py-2">{message}</div>}
        {error && <div className="alert alert-danger py-2">{error}</div>}

        <div className="row g-4 mb-4">
          <div className="col-12 col-xl-7">
            <div className="border rounded-4 p-4 h-100">
              <div className="fw-semibold mb-1">{labels.addTitle}</div>
              <div className="text-muted small mb-3">{labels.subtitle}</div>

              <form onSubmit={handleSubmit} className="row g-3">
                <div className="col-12">
                  <label className="form-label">{labels.search}</label>
                  <GoogleAddressAutocomplete
                    className="form-control rounded-3"
                    placeholder={labels.search}
                    value={form.addressSearch}
                    onChange={handleAddressSearchChange}
                    onSelect={handleAddressSelect}
                    onReadyChange={setGoogleReady}
                    required
                    languageCode={isAr ? "ar" : "en"}
                    statusTexts={{
                      readyHint: labels.searchHint,
                      loadFailed: labels.searchFailed,
                      loading: labels.searchLoading,
                      noResults: labels.searchNoResults,
                    }}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">{labels.street}</label>
                  <input
                    required
                    name="street"
                    className="form-control rounded-3"
                    value={form.street}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">{labels.neighborhood}</label>
                  <input
                    name="neighborhood"
                    className="form-control rounded-3"
                    value={form.neighborhood}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">{labels.city}</label>
                  <input
                    required
                    name="city"
                    className="form-control rounded-3"
                    value={form.city}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">{labels.notes}</label>
                  <input
                    name="address_text"
                    className="form-control rounded-3"
                    value={form.address_text}
                    onChange={handleChange}
                    placeholder={labels.notesHint}
                  />
                </div>

                <div className="col-12 d-flex flex-wrap gap-2">
                  <button type="submit" className="btn btn-dark rounded-pill px-4" disabled={saving || !googleReady}>
                    {saving ? labels.saving : labels.save}
                  </button>
                  {editingId ? (
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={resetForm}>
                      {labels.cancel}
                    </button>
                  ) : null}
                </div>
              </form>
            </div>
          </div>

          <div className="col-12 col-xl-5">
            <div className="border rounded-4 p-4 h-100 bg-light-subtle">
              <div className="fw-semibold mb-3">{labels.preview}</div>

              {addressPreview ? (
                <div className="border rounded-4 bg-white p-3">
                  <div className="fw-semibold mb-2">{addressPreview}</div>
                  {form.address_text ? (
                    <div className="small text-muted">{form.address_text}</div>
                  ) : null}
                </div>
              ) : (
                <div className="small text-muted">{labels.noPreview}</div>
              )}

              {defaultAddress ? (
                <div className="mt-4">
                  <div className="small text-uppercase text-muted mb-2">
                    {isAr ? "العنوان الافتراضي الحالي" : "Current default address"}
                  </div>
                  <div className="border rounded-4 bg-white p-3">
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div className="fw-semibold">
                        {[defaultAddress.street, defaultAddress.neighborhood, defaultAddress.city]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                      <span className="badge bg-success">{labels.defaultLabel}</span>
                    </div>
                    {defaultAddress.address_text ? (
                      <div className="small text-muted">{defaultAddress.address_text}</div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-muted mb-0">{isAr ? "جارٍ التحميل..." : "Loading..."}</p>
        ) : addresses.length === 0 ? (
          <p className="text-muted mb-0">
            {labels.empty}
          </p>
        ) : (
          <div className="row g-3">
            {addresses.map((address) => (
              <div className="col-12 col-lg-6" key={address.id}>
                <div className="border rounded-4 p-3 h-100 bg-light">
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div className="fw-semibold">
                      {[address.street, address.neighborhood, address.city]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                    {address.is_default ? (
                      <span className="badge bg-success">{labels.defaultLabel}</span>
                    ) : null}
                  </div>
                  {address.address_text ? (
                    <div className="text-muted small mb-2">{address.address_text}</div>
                  ) : null}
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-dark"
                      onClick={() => handleEdit(address)}
                    >
                      {labels.edit}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleSetDefault(address.id)}
                      disabled={Boolean(address.is_default)}
                    >
                      {labels.makeDefault}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(address.id)}
                    >
                      {labels.delete}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
